import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { onboardingApi } from '@/api/endpoints'
import { Button, EnterChip } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import { useEnterToContinue, useSaveStep } from '../useWizard'
import { WizardActions, WizardPanel } from '../WizardShell'

const ATS_TAGS = ['Workday', 'iCIMS', 'Oracle']

const RULES = [
  { key: 'minLength', label: 'At least 12 characters', test: (v: string) => v.length >= 12 },
  { key: 'lowercase', label: 'At least one lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { key: 'uppercase', label: 'At least one uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { key: 'digit', label: 'At least one number', test: (v: string) => /\d/.test(v) },
  {
    key: 'special',
    label: 'At least one special character',
    test: (v: string) => /[^A-Za-z0-9]/.test(v),
  },
] as const

interface Props {
  onDone: () => void
}

export function PasswordStep({ onDone }: Props) {
  const save = useSaveStep('application_password')
  const [password, setPassword] = useState('')
  const [visible, setVisible] = useState(false)
  const [copied, setCopied] = useState(false)

  // Validated client-side for instant feedback; the server re-checks the same
  // rules on save, so the UI is a convenience and never the gate.
  const results = useMemo(
    () => RULES.map((rule) => ({ ...rule, passed: rule.test(password) })),
    [password],
  )
  const isValid = results.every((rule) => rule.passed)

  const generate = useMutation({
    mutationFn: () => onboardingApi.generatePassword(16),
    onSuccess: (result) => {
      setPassword(result.password)
      setVisible(true)
    },
    onError: () => toast.error('Could not generate a password'),
  })

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Copy failed', 'Select the text and copy it manually.')
    }
  }

  const submit = () => {
    if (!isValid || save.isPending) return
    save.mutate({ password }, { onSuccess: onDone })
  }
  useEnterToContinue(submit, isValid)

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-1.5">
          {ATS_TAGS.map((tag) => (
            <span key={tag} className="rounded-[7px] bg-surface-muted px-2.5 py-1 text-[13px] text-ink-soft md:bg-ink/[0.05]">
              {tag}
            </span>
          ))}
          <span className="text-[13px] text-ink-muted">+ more</span>
        </div>

        <WizardPanel>
          <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-muted/70 px-4 py-1.5 sm:px-5">
            <h2 className="label-eyebrow">Password</h2>
            <button
              type="button"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
              className="-mr-2 inline-flex h-10 items-center rounded-field px-2 text-[13px] font-medium text-ink-soft transition-colors hover:text-ink disabled:opacity-50"
            >
              {generate.isPending ? 'Generating…' : 'Generate strong password'}
            </button>
          </div>

          <div className="px-4 pt-3 sm:px-5">
            <div className="flex items-center gap-1 border-b border-line pb-1.5">
              <input
                type={visible ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your application password"
                autoComplete="new-password"
                autoCapitalize="none"
                spellCheck={false}
                aria-label="Application password"
                aria-invalid={password.length > 0 && !isValid}
                className="min-h-[44px] min-w-0 flex-1 border-0 bg-transparent py-1.5 text-lg text-ink outline-none placeholder:text-ink-faint"
              />
              {password && (
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex h-11 shrink-0 items-center rounded-field px-2 text-[13px] font-medium text-ink-soft transition-colors hover:text-ink"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                aria-pressed={visible}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-field text-ink-muted transition-colors hover:text-ink"
              >
                <EyeIcon off={visible} />
                <span className="sr-only">{visible ? 'Hide characters' : 'Show characters'}</span>
              </button>
            </div>

            <ul className="space-y-2 py-4">
              {results.map((rule) => (
                <li key={rule.key} className="flex items-center gap-2.5 text-[14px]">
                  <RuleIcon passed={rule.passed} />
                  <span className={cn(rule.passed ? 'text-ink' : 'text-ink-muted')}>{rule.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </WizardPanel>
      </div>

      <WizardActions
        aside={
          <span className="inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
            Encrypted before save
          </span>
        }
      >
        <Button
          size="lg"
          className="flex-1 sm:flex-none"
          onClick={submit}
          disabled={!isValid}
          loading={save.isPending}
        >
          Save &amp; continue
          <EnterChip />
        </Button>
      </WizardActions>
    </>
  )
}

function RuleIcon({ passed }: { passed: boolean }) {
  if (!passed) {
    return (
      <span
        className="h-[17px] w-[17px] shrink-0 rounded-full border-[1.5px] border-line-strong"
        aria-hidden="true"
      />
    )
  }
  return (
    <span className="grid h-[17px] w-[17px] shrink-0 place-items-center rounded-full bg-brand text-oncolor" aria-hidden="true">
      <svg className="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none">
        <path d="m2.5 6.2 2.2 2.2L9.5 3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M1.8 10S4.7 4.8 10 4.8 18.2 10 18.2 10 15.3 15.2 10 15.2 1.8 10 1.8 10Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.4" />
      {off && <path d="m3 3 14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />}
    </svg>
  )
}
